import http from "@/services/http";
import { phoneMockManager } from "../mock/auth";

// 发送验证码参数 - 只需要手机号
export type SendCodeParams = {
  phone: string;
};

// 登录参数 - 手机号和验证码
export type LoginWithCodeParams = {
  phone: string;
  code: string;
  channel_code?: string;
};

// 邮箱密码登录参数
export type LoginWithEmailParams = {
  email: string;
  password: string;
  channel_code?: string;
};

import { featureFlags } from "@/config/env";

const useMock = featureFlags.enableMock;

async function sendLoginCodeMock(params: SendCodeParams) {
  console.log("[mock] send-code", params);

  // 验证手机号格式
  if (!params.phone || !/^1[3-9]\d{9}$/.test(params.phone)) {
    return { data: { code: 400, message: "手机号格式不正确" } } as any;
  }

  // 使用phoneMockManager发送验证码
  const result = await phoneMockManager.sendCode({ phone: params.phone });

  if (result.code === 200) {
    return { data: { code: 200, message: result.message } } as any;
  } else {
    return { data: { code: result.code, message: result.message } } as any;
  }
}

async function loginWithCodeMock(params: LoginWithCodeParams) {
  console.log("[mock] login-code", params);

  // 验证必填参数
  if (!params.phone || !params.code) {
    return { data: { code: 400, message: "手机号和验证码不能为空" } } as any;
  }

  // 使用phoneMockManager进行登录
  const result = await phoneMockManager.login({
    mobile: params.phone,
    smsCode: params.code,
  });

  if (result.code === 200) {
    return {
      data: {
        code: 200,
        message: "登录成功",
        data: { token: result.data.token, user: result.data.user },
      },
    } as any;
  } else {
    return { data: { code: result.code, message: result.message } } as any;
  }
}

// 发送登录验证码
export async function sendLoginCode(params: SendCodeParams) {
  if (useMock) return await sendLoginCodeMock(params);
  // 后端字段：Mobile（区分大小写）
  const body = { mobile: params.phone };
  const res = await http.post("v1/sms/send", body);
  return res;
}

// 验证码登录
export async function loginWithCode(params: LoginWithCodeParams) {
  if (useMock) return await loginWithCodeMock(params);
  // 后端字段：Mobile、SmsCode（区分大小写）
  const body = {
    mobile: params.phone,
    sms_code: params.code,
    channel_code: params.channel_code,
  };
  const res = await http.post("v1/user/login", body);
  return res;
}

// 邮箱密码登录 Mock
async function loginWithEmailMock(params: LoginWithEmailParams) {
  console.log("[mock] login-email", params);

  // 验证必填参数
  if (!params.email || !params.password) {
    return { data: { code: 400, message: "邮箱和密码不能为空" } } as any;
  }

  // 验证邮箱格式
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(params.email)) {
    return { data: { code: 400, message: "邮箱格式不正确" } } as any;
  }

  // Mock 登录逻辑：简单验证，任何邮箱+密码都可以登录（演示用）
  // 实际应该调用 phoneMockManager 或创建新的邮箱登录管理器
  const mockToken = `mock_token_${Date.now()}`;
  const mockUser = {
    id: "mock_user_" + Date.now(),
    email: params.email,
    mobile: "",
    name: params.email.split("@")[0],
  };

  return {
    data: {
      code: 200,
      message: "登录成功",
      data: {
        token: mockToken,
        user: mockUser,
      },
    },
  } as any;
}

// 邮箱密码登录
export async function loginWithEmail(params: LoginWithEmailParams) {
  if (useMock) return await loginWithEmailMock(params);
  const body = {
    email: params.email,
    password: params.password,
    channel_code: params.channel_code || "",
  };
  const res = await http.post("v1/user/password-login", body);
  return res;
}

// ==================== 邮箱注册相关 API ====================

// 发送邮箱验证码参数
export type SendEmailCodeParams = {
  email: string;
  channel_code?: string;
};

// 邮箱注册参数
export type RegisterWithEmailParams = {
  email: string;
  email_code: string;
  password: string;
  channel_code?: string;
};

// 发送邮箱验证码
export async function sendEmailCode(params: SendEmailCodeParams) {
  if (useMock) {
    // Mock 实现
    console.log("[mock] send-email-code", params);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return {
      data: {
        code: 200,
        message: "验证码已发送，请查收邮件",
        data: { success: true, log_id: `mock_${Date.now()}` },
      },
    } as any;
  }

  const body = {
    email: params.email,
    channel_code: params.channel_code || "",
  };
  const res = await http.post("v1/email/send-code", body);
  return res;
}

// 邮箱注册
export async function registerWithEmail(params: RegisterWithEmailParams) {
  if (useMock) {
    // Mock 实现
    console.log("[mock] register-with-email", params);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    const mockToken = `mock_token_${Date.now()}`;
    const mockUser = {
      id: `mock_user_${Date.now()}`,
      email: params.email,
      account: params.email,
      mobile: "",
      name: params.email.split("@")[0],
      avatar: "",
      status: 1,
      is_beta: 0,
      channel_code: params.channel_code || "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_login_at: new Date().toISOString(),
      last_login_ip: "127.0.0.1",
    };
    return {
      data: {
        code: 200,
        message: "注册成功",
        data: { token: mockToken, user: mockUser },
      },
    } as any;
  }

  const body = {
    email: params.email,
    email_code: params.email_code,
    password: params.password,
    channel_code: params.channel_code || "",
  };
  const res = await http.post("v1/user/register", body);
  return res;
}

// ==================== 重置密码相关 API ====================

// 忘记密码参数
export type ForgotPasswordParams = {
  email: string;
};

// 验证重置 token 参数
export type VerifyResetTokenParams = {
  token: string;
};

// 重置密码参数
export type ResetPasswordParams = {
  token: string;
  new_password: string;
};

// 忘记密码（发送重置邮件）
export async function forgotPassword(params: ForgotPasswordParams) {
  if (useMock) {
    // Mock 实现
    console.log("[mock] forgot-password", params);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return {
      data: {
        code: 200,
        message: "如果该邮箱已注册，重置密码邮件已发送，请查收",
        data: null,
      },
    } as any;
  }

  const body = { email: params.email };
  const res = await http.post("v1/user/forgot-password", body);
  return res;
}

// 验证重置密码 Token
export async function verifyResetToken(params: VerifyResetTokenParams) {
  if (useMock) {
    // Mock 实现
    console.log("[mock] verify-reset-token", params);
    await new Promise((resolve) => setTimeout(resolve, 500));
    return {
      data: {
        code: 200,
        message: "验证成功",
        data: { valid: true, email: "mock@example.com" },
      },
    } as any;
  }

  const res = await http.get("v1/user/verify-reset-token", {
    params: { token: params.token },
  });
  return res;
}

// 重置密码
export async function resetPassword(params: ResetPasswordParams) {
  if (useMock) {
    // Mock 实现
    console.log("[mock] reset-password", params);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return {
      data: {
        code: 200,
        message: "密码重置成功",
        data: null,
      },
    } as any;
  }

  const body = {
    token: params.token,
    new_password: params.new_password,
  };
  const res = await http.post("v1/user/reset-password", body);
  return res;
}
